import { setupListeners } from '@reduxjs/toolkit/query';
import { ConfigCatProvider, DataGovernance, PollingMode } from 'configcat-react';
import { createRoot } from 'react-dom/client';
import { Provider } from 'react-redux';
import { RouterProvider } from 'react-router-dom';

import { config } from './config.ts';
import { main } from './configuration';
import { isDev } from './configuration/env.ts';
import { I18NProvider } from './configuration/lang/I18NProvider.tsx';
import { handleLoginRedirect } from './configuration/login/redirect.ts';
import { appRouter } from './routes/Router.tsx';
import { store } from './store/store.ts';

import '@rio-cloud/iframe-resizer/child';
import './configuration/datadog/setup.ts';

// Needed for RTK Query lifecycle/focus features
setupListeners(store.dispatch);

const renderApplication = () => {
    // biome-ignore lint/style/noNonNullAssertion: always there
    return createRoot(document.getElementById('root')!).render(
        <Provider store={store}>
            <I18NProvider>
                <ConfigCatProvider
                    sdkKey={config.configCat.apiKey}
                    pollingMode={PollingMode.AutoPoll}
                    options={{
                        cacheTimeToLiveSeconds: config.configCat.cacheTimeInSeconds,
                        dataGovernance: DataGovernance.EuOnly,
                    }}
                >
                    <RouterProvider router={appRouter} />
                </ConfigCatProvider>
            </I18NProvider>
        </Provider>
    );
};

async function enableMocking() {
    if (!isDev) {
        return;
    }

    const { worker } = await import('./mocks/browser');

    // `worker.start()` returns a Promise that resolves
    // once the Service Worker is up and ready to intercept requests.
    return worker.start({
        serviceWorker: { url: 'mockServiceWorker.js' },
        onUnhandledRequest: 'bypass',
    });
}

if (window.location.href.startsWith(config.login.redirectUri as string)) {
    handleLoginRedirect();
} else {
    enableMocking().then(() => {
        void main(renderApplication);
    });
}
